import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTrash, FaPlus, FaUser } from 'react-icons/fa';
import SubsectionPanel from '../../common/SubsectionPanel/SubsectionPanel';
import GenericFlyoutPanel from '../../common/GenericFlyoutPanel/GenericFlyoutPanel';
import FormGenerator, { FormFieldConfig } from '../../common/FormGenerator/FormGenerator';
import DetailedListComponent from '../../common/DetailedList/DetailedListComponent';
import { TeamMember, TeamMemberDataProvider } from '../../../data/TeamMemberDataProvider';
import { UsersRestDataProvider, User } from '../../../data/UsersRestDataProvider';
import InfoPanel from '../../common/InfoPanel/InfoPanel';

const TeamPeople: React.FC<{ teamId: string }> = ({ teamId }) => {
  const { t } = useTranslation(['common', 'screens']);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  // Auto-select first member if none selected
  React.useEffect(() => {
    if (!selectedId && members.length > 0) {
      setSelectedId(members[0].id ?? null);
    }
    if (members.length === 0) {
      setSelectedId(null);
    }
  }, [members, selectedId]);

  const selected = members.find(m => m.id === selectedId) || null;

  // Helper subcomponents for lists
  const [skills, setSkills] = useState<any[]>(selected?.skills || []);
  const [certifications, setCertifications] = useState<any[]>(selected?.certifications || []);
  const [experiences, setExperiences] = useState<any[]>(selected?.experiences || []);

  React.useEffect(() => {
    setSkills(selected?.skills || []);
    setCertifications(selected?.certifications || []);
    setExperiences(selected?.experiences || []);
  }, [selected]);

  const proficiencyLabels = [
    t('screens:teamPeople.proficiencyLevels.0'),
    t('screens:teamPeople.proficiencyLevels.1'),
    t('screens:teamPeople.proficiencyLevels.2'),
    t('screens:teamPeople.proficiencyLevels.3'),
    t('screens:teamPeople.proficiencyLevels.4'),
    t('screens:teamPeople.proficiencyLevels.5'),
  ];

  const skillsColumns = [
    { key: 'skillName', header: t('screens:teamPeople.skillsColumns.skillName') },
    {
      key: 'proficiencyLevel',
      header: t('screens:teamPeople.skillsColumns.proficiencyLevel'),
      render: (item: any) => (
        <span>{item.proficiencyLevel} - {proficiencyLabels[item.proficiencyLevel]}</span>
      ),
    },
  ];

  // For the flyout form, use a custom field for proficiencyLevel
  const skillFields: FormFieldConfig[] = [
    { key: 'skillName', label: t('screens:teamPeople.skillFields.skillName'), type: 'string', required: true },
    {
      key: 'proficiencyLevel',
      label: t('screens:teamPeople.skillFields.proficiencyLevel'),
      type: 'slider',
      min: 1,
      max: 5,
      options: proficiencyLabels.slice(1).map((label, idx) => ({ label, value: idx + 1 })),
      required: true,
    },
  ];

  const certColumns = [
    { key: 'certificationName', header: t('screens:teamPeople.certColumns.certification') },
    { key: 'issuingOrganization', header: t('screens:teamPeople.certColumns.organization') },
    { key: 'issuedDate', header: t('screens:teamPeople.certColumns.issued') },
    { key: 'expiryDate', header: t('screens:teamPeople.certColumns.expires') },
  ];

  const certFields: FormFieldConfig[] = [
    { key: 'certificationName', label: t('screens:teamPeople.certFields.certification'), type: 'string', required: true },
    { key: 'issuingOrganization', label: t('screens:teamPeople.certFields.organization'), type: 'string' },
    { key: 'issuedDate', label: t('screens:teamPeople.certFields.issued'), type: 'date' },
    { key: 'expiryDate', label: t('screens:teamPeople.certFields.expires'), type: 'date' },
  ];

  const expColumns = [
    { key: 'title', header: t('screens:teamPeople.expColumns.title') },
    { key: 'companyName', header: t('screens:teamPeople.expColumns.company') },
    { key: 'fromDate', header: t('screens:teamPeople.expColumns.from') },
    { key: 'toDate', header: t('screens:teamPeople.expColumns.to') },
    { key: 'positionOrRole', header: t('screens:teamPeople.expColumns.role') },
    { key: 'description', header: t('screens:teamPeople.expColumns.description') },
  ];

  const expFields: FormFieldConfig[] = [
    { key: 'title', label: t('screens:teamPeople.expFields.title'), type: 'string', required: true },
    { key: 'companyName', label: t('screens:teamPeople.expFields.company'), type: 'string' },
    { key: 'fromDate', label: t('screens:teamPeople.expFields.from'), type: 'date' },
    { key: 'toDate', label: t('screens:teamPeople.expFields.to'), type: 'date' },
    { key: 'positionOrRole', label: t('screens:teamPeople.expFields.role'), type: 'string' },
    { key: 'description', label: t('screens:teamPeople.expFields.description'), type: 'string', multiline: true, rows: 3 },
  ];

  const teamMemberProvider = React.useMemo(() => new TeamMemberDataProvider(), []);
  const usersProvider = React.useMemo(() => new UsersRestDataProvider(), []);

  React.useEffect(() => {
    teamMemberProvider.getAll().then(setMembers);
    usersProvider.getAll().then(setUsers);
  }, [teamId]);

  const handleAdd = async (member: Partial<TeamMember>) => {
    const created = await teamMemberProvider.create({
      ...member,
      teamId,
      skills,
      certifications,
      experiences,
    } as TeamMember);
    setMembers(m => [...m, created]);
  };

  const handleUpdate = async (member: TeamMember) => {
    if (!member.id) return;
    await teamMemberProvider.update(member.id, {
      ...member,
      skills,
      certifications,
      experiences,
    });
    setMembers(m => m.map(x => x.id === member.id ? { ...member, skills, certifications, experiences } : x));
  };

  const handleDelete = async (id: string) => {
    await teamMemberProvider.delete(id);
    setMembers(m => m.filter(x => x.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  // Dynamically build memberFields to include userId dropdown
  const memberFields: FormFieldConfig[] = [
    {
      key: 'userId',
      label: t('screens:teamPeople.memberFields.account'),
      type: 'select',
      required: false,
      options: users.map(u => ({ label: u.username, value: u.id }))
    },
    { key: 'firstName', label: t('screens:teamPeople.memberFields.firstName'), type: 'string', required: true },
    { key: 'lastName', label: t('screens:teamPeople.memberFields.lastName'), type: 'string', required: true },
    { key: 'costRate', label: t('screens:teamPeople.memberFields.costRate'), type: 'number' },
    { key: 'personalDescription', label: t('screens:teamPeople.memberFields.personalDescription'), type: 'string', multiline: true, rows: 3 },
    // Add more fields for skills, certifications, experiences
  ];

  return (
    <div className="row">
      <div style={{"padding": "0px", "margin": "0px 0px 12px 0px"}}>
        <InfoPanel header={t('screens:teamPeople.managingTeams')}>
          {t('screens:teamPeople.managingTeamsDescription')}
        </InfoPanel>
      </div>
      <div className="col-4 border-end pe-0" style={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">{t('screens:teamPeople.people')}</h5>
          <button className="btn btn-sm btn-outline-primary" onClick={() => setShowAdd(true)} title={t('screens:teamPeople.addPerson')}>
            <FaPlus />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <ul className="list-group team-people-list">
            {members.length === 0 ? (
              <li className="list-group-item text-center text-muted py-5 d-flex flex-column justify-content-center align-items-center" style={{ background: '#f8f9fa', border: '1px dashed #ced4da' }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}><FaUser /></div>
                <div className="fw-bold mb-2" style={{ fontSize: 18 }}>{t('screens:teamPeople.noPeople')}</div>
                <div className="mb-2" style={{ maxWidth: 220 }}>{t('screens:teamPeople.noPeopleHint')} <span className="fw-bold">{t('screens:teamPeople.addPersonHint')}</span> {t('screens:teamPeople.above')}</div>
              </li>
            ) : (
              members.map(member => (
                <li
                  key={member.id ?? ''}
                  className={`list-group-item d-flex align-items-center justify-content-between ${selectedId === (member.id ?? '') ? 'active' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedId(member.id ?? null)}
                >
                  <span className="d-flex align-items-center">
                    <span className="avatar bg-light rounded-circle me-2" style={{ width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FaUser />
                    </span>
                    {/* Display first and last name if available, otherwise fallback to userId */}
                    {member.firstName || member.lastName ? (
                      <span>{member.firstName} {member.lastName}</span>
                    ) : (
                      member.userId ?? <span className="text-muted">{t('screens:teamPeople.noUser')}</span>
                    )}
                  </span>
                  <button className="btn btn-sm btn-outline-danger ms-2" onClick={e => { e.stopPropagation(); if (member.id) handleDelete(member.id); }} title={t('screens:teamPeople.remove')}>
                    <FaTrash />
                  </button>
                </li>
              )))
            }
            
          </ul>
        </div>
        <GenericFlyoutPanel open={showAdd} onClose={() => setShowAdd(false)} title={t('screens:teamPeople.addPersonSubmit')}>
          <FormGenerator
            fields={memberFields}
            initialData={{}}
            onSubmit={data => { handleAdd({ ...data, teamId }); setShowAdd(false); }}
            submitLabel={t('screens:teamPeople.addPersonSubmit')}
          />
        </GenericFlyoutPanel>
      </div>
      <div className="col-8 ps-4">
        {selected ? (
          <>
            <SubsectionPanel title={t('screens:teamPeople.editPerson')}>
              <FormGenerator
                fields={memberFields}
                initialData={selected}
                onSubmit={handleUpdate}
                submitLabel={t('common:save')}
              />
            </SubsectionPanel>
            <SubsectionPanel title={t('screens:teamPeople.skills')}>
              <DetailedListComponent
                columns={skillsColumns}
                items={skills}
                rowKey={item => item.id || item.skillName}
                dataSource={{ data: skills, setData: setSkills }}
                formFields={skillFields}
              />
            </SubsectionPanel>
            <SubsectionPanel title={t('screens:teamPeople.certifications')}>
              <DetailedListComponent
                columns={certColumns}
                items={certifications}
                rowKey={item => item.id || item.certificationName}
                dataSource={{ data: certifications, setData: setCertifications }}
                formFields={certFields}
              />
            </SubsectionPanel>
            <SubsectionPanel title={t('screens:teamPeople.workExperience')}>
              <DetailedListComponent
                columns={expColumns}
                items={experiences}
                rowKey={item => item.id || item.title}
                dataSource={{ data: experiences, setData: setExperiences }}
                formFields={expFields}
              />
            </SubsectionPanel>
          </>
        ) : (
          <div className="text-muted">{t('screens:teamPeople.selectPerson')}</div>
        )}
      </div>
    </div>
  );
};

export default TeamPeople;
